import getElementFromComponent from 'utils/getElementFromComponent';
import {
  __,
  sprintf,
  setLocaleData,
} from '@wordpress/i18n';

/**
 * Component for filtering milestones.
 */
export default class FilterMilestones {
  /**
   * Get unique ids from an array.
   *
   * @param {Array} Array with duplicate values.
   * @returns {Array} Array with unique values.
   */
  static unique(array) {
    return [...new Set(array)];
  }

  /**
   * Get base URL.
   *
   * @returns {String}
   */
  static getBaseUrl() {
    return new URL(window.location);
  }

  /**
   * Check if item has ids.
   *
   * @param {Node} item Item node.
   * @param {Array} ids Array of ids.
   */
  static hasTypes(item, ids, type) {
    const {
      [type]: dataType,
    } = item.dataset;

    const [hasTypes] = ids
      .filter((value) => dataType
        .includes(Number(value)));

    return (undefined !== hasTypes);
  }

  /**
   * Start the component.
   */
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    // Register text domain.
    setLocaleData({ '': {} }, 'sciencenews');

    // Bind class methods.
    this.init = this.init.bind(this);
    this.handleFilter = this.handleFilter.bind(this);
    this.getParamIds = this.getParamIds.bind(this);
    this.filterPosts = this.filterPosts.bind(this);
    this.pushHistory = this.pushHistory.bind(this);
    this.updateState = this.updateState.bind(this);
    this.getAllInputIds = this.getAllInputIds.bind(this);
    this.getAllActiveInputIds = this.getAllActiveInputIds.bind(this);
    this.handleSelected = this.handleSelected.bind(this);
    this.createItem = this.createItem.bind(this);
    this.updateTotalCount = this.updateTotalCount.bind(this);

    // Get lists of posts.
    this.items = Array.from(getElementFromComponent(
      'list-item',
      'snc-milestones-posts-section',
      true,
    ));

    // Get total count from h2 heading.
    this.totalCount = getElementFromComponent(
      'heading',
      'snc-milestones-archive',
    ).querySelector('i');

    // Hold list of searched ids.
    this.filteredIds = [];

    // Hold list of inputs.
    this.inputs = Array.from(this.element.querySelectorAll('input'));

    // Hold object of label text references.
    this.labels = Array.from(this.element.querySelectorAll('label'))
      .reduce((acc, label) => {
        const labelFor = label.getAttribute('for');
        return { ...acc, [labelFor]: label.innerText.trim() };
      }, {});

    // Component type.
    this.type = this.element.dataset.filterType;

    // Get h2 heading child selected spans.
    this.selectedIds = getElementFromComponent(
      `selected-${this.type}`,
      'snc-milestones-archive',
    );

    const {
      hide,
      linkActive,
    } = this.options;

    Object.assign(this, {
      hide,
      linkActive,
    });

    // Handle init.
    this.init();

    // Update posts.
    this.element.addEventListener('change', this.handleFilter);
  }

  /**
   * Handle filtered items.
   *
   * @param {Event} event The Event object.
   */
  handleFilter(event) {
    const { target } = event;

    // Get object ID.
    const objectID = target.id;

    // Reset?
    let isReset = this.type === objectID;

    if (isReset) {
      // Reset active/checked inputs.
      this.inputs.forEach((input, index) => {
        // eslint-disable-next-line no-param-reassign
        input.checked = 0 === index;
      });

      this.filteredIds = [];
    } else {
      // Unchecked first one if necessary.
      if (this.inputs[0].checked) {
        this.inputs[0].checked = false;
      }

      if (! target.checked) {
        // Set the first one active if there is no other.
        if (! this.getAllActiveInputIds().length) {
          this.inputs[0].checked = true;

          // Also, set to true to reset the state.
          isReset = true;

          // Empty list of filtered ids.
          this.filteredIds = [];
        }

        // Remove current item ID from the list.
        this.filteredIds = this.filteredIds
          .filter((id) => id !== objectID);
      } else {
        // Add current ID to the list.
        this.filteredIds.push(objectID);
      }
    }

    // Update history state and URL params.
    this.pushHistory(isReset);

    // Filter posts.
    this.filterPosts();

    // Update Selected heading.
    if (null !== this.selectedIds) {
      this.handleSelected();
    }

    // Set total count.
    this.updateTotalCount();
  }

  /**
   * Handle selected items.
   */
  handleSelected() {
    // Get the span.
    const span = this.selectedIds.querySelector('span');

    // Remove it.
    span.remove();

    //  Reset? Add default one.
    if (! this.filteredIds.length) {
      this.createItem(sprintf(
        // translators: %1$s Name of the current component.
        __('All %1$s', 'sciencenews'),
        this.type[0].toUpperCase() + this.type.slice(1),
      ));
    } else {
      const labelNames = this.filteredIds.map((item) => this.labels[item]);

      // Recreate it with new or without old ones.
      this.createItem(labelNames.join('; '));
    }
  }

  /**
   * Get all input IDs.
   *
   * @returns {Array} An array with all input IDs.
   */
  getAllInputIds() {
    return this.inputs
      .filter((input) => (this.type !== input.id) && 0 !== Number(input.id))
      .map((item) => item.id);
  }

  /**
   * Get all active input Ids.
   *
   * @returns {Array} An array with all active input IDs.
   */
  getAllActiveInputIds() {
    return this.inputs
      .filter((input) => (true === input.checked) && 0 !== Number(input.id))
      .map((item) => item.id);
  }

  /**
   * Get ids from the url param.
   *
   * @returns {Array} An array of param IDs.
   */
  getParamIds() {
    const ids = this.constructor.getBaseUrl().searchParams.get(this.type);

    // Bail early.
    if (null === ids) {
      return [];
    }

    // Get ids.
    return this.constructor.unique(ids.split(','))
      .filter((item) => 0 !== Number(item));
  }

  /**
   * Handle component initialization.
   */
  init() {
    const ids = this.getParamIds();
    const currentState = window.history.state;
    const activeInputIds = (0 < ids.length) ? this.getAllActiveInputIds() : [];
    let newState = {
      items: {},
    };

    if (null === currentState) {
      newState = {
        items: {
          [this.type]: activeInputIds,
        },
      };
    } else {
      newState = {
        items: this.updateState(currentState.items, activeInputIds),
      };
    }

    // Update state.
    window.history.replaceState(newState, '');

    // Set all active inputs.
    this.filteredIds = activeInputIds;

    // Filter posts.
    this.filterPosts();

    // Set total count.
    this.updateTotalCount();
  }

  /**
   * Update the URL.
   *
   * @param {Boolean} clear Are we clearing the current component state?
   */
  pushHistory(clear = false) {
    const currentState = window.history.state;
    const baseUrl = this.constructor.getBaseUrl();

    if (true === clear) {
      baseUrl.searchParams.delete(this.type);
    } else {
      baseUrl.searchParams.set(this.type, this.filteredIds.join(','));
    }

    // Update history state.
    window.history.replaceState(
      {
        items: this.updateState(currentState.items, this.filteredIds),
      },
      '',
      baseUrl,
    );
  }

  /**
   * Update total count.
   */
  updateTotalCount() {
    this.totalCount.innerText = this.items
      .filter((item) => ! item.classList.contains(this.hide))
      .length;
  }

  /**
   * Filter posts.
   */
  filterPosts() {
    const {
      items: {
        themes: themeIds = [],
        connections: connectionIds = [],
      },
    } = window.history.state;

    const emptyState = (! themeIds.length && ! connectionIds.length);
    const bothTypes = (themeIds.length && connectionIds.length);

    /**
     * Brief explanation of how this is working.
     *
     * We hide the items by default. And we show them based on:
     *
     * - show if: themes and connections are empty (clean state).
     * - show if: we have both types in the state, and the current item have ids
     * from both types.
     * - show if: the current item have ids from one of the types in the state
     * and the state has only one type.
     */
    this.items.forEach((item) => {
      let hideItem = true;

      const showTheme = this.constructor
        .hasTypes(item, themeIds, 'themes');

      const showConnection = this.constructor
        .hasTypes(item, connectionIds, 'connections');

      switch (true) {
        case emptyState:
          hideItem = false;
          break;

        case bothTypes && (showTheme && showConnection):
          hideItem = false;
          break;

        case (! bothTypes && (showTheme || showConnection)):
          hideItem = false;
          break;

        default:
          hideItem = true;
      }

      item.classList.toggle(this.hide, hideItem);
    });
  }

  /**
   * Update state of the current component.
   *
   * @param {Object} items
   * @param {Array} ids
   * @returns {Object}
   */
  updateState(items, ids) {
    return Object.assign(items, {
      [this.type]: ids,
    });
  }

  /**
   * Create Node.
   *
   * @param {String} itemLabel Item label.
   */
  createItem(itemLabel) {
    const span = document.createElement('span');
    const spanLabel = document.createTextNode(itemLabel);

    span.appendChild(spanLabel);

    this.selectedIds.appendChild(span);
  }
}
